import type { EnterpriseAccountSummary } from '../services/api';

export type EnterprisePeriod = 'week' | 'month' | 'custom' | 'all';

const ACTIVE_ENTERPRISE_STORAGE_KEY = 'wemove.activeEnterpriseAccountId';

interface EnterpriseAccessUser {
    enterpriseAdminEnabled?: boolean;
    enterpriseAdminOf?: string[];
}

/** Org admin dashboard access requires explicit enterprise assignment — not super_admin role alone. */
export function hasAssignedEnterpriseAdminAccess(
    authUser?: EnterpriseAccessUser | null
): boolean {
    if (!authUser || authUser.enterpriseAdminEnabled === false) {
        return false;
    }
    return (authUser.enterpriseAdminOf?.length || 0) > 0;
}

export function filterEnterpriseAccountsForUser(
    accounts: EnterpriseAccountSummary[],
    assignedAccountIds?: string[]
): EnterpriseAccountSummary[] {
    if (!assignedAccountIds || assignedAccountIds.length === 0) {
        return [];
    }

    const assignedIdSet = new Set(assignedAccountIds);
    return accounts.filter((accountRecord) => assignedIdSet.has(accountRecord.id));
}

export function pickEnterpriseAccount(
    accounts: EnterpriseAccountSummary[],
    searchParams?: URLSearchParams
): EnterpriseAccountSummary | null {
    if (accounts.length === 0) {
        return null;
    }

    const accountParam = searchParams?.get('account');
    if (accountParam) {
        const matchedAccount = accounts.find((accountRecord) => accountRecord.id === accountParam);
        if (matchedAccount) {
            return matchedAccount;
        }
    }

    const slugParam = searchParams?.get('enterprise')?.trim().toLowerCase();
    if (slugParam) {
        const matchedAccount = accounts.find((accountRecord) => accountRecord.slug === slugParam);
        if (matchedAccount) {
            return matchedAccount;
        }
    }

    try {
        const storedAccountId = localStorage.getItem(ACTIVE_ENTERPRISE_STORAGE_KEY);
        if (storedAccountId) {
            const matchedAccount = accounts.find((accountRecord) => accountRecord.id === storedAccountId);
            if (matchedAccount) {
                return matchedAccount;
            }
        }
    } catch {
        // localStorage may be unavailable in private browsing
    }

    return accounts[0];
}

export function persistActiveEnterpriseAccountId(accountId: string): void {
    try {
        localStorage.setItem(ACTIVE_ENTERPRISE_STORAGE_KEY, accountId);
    } catch {
        // localStorage may be unavailable in private browsing
    }
}

export interface EnterpriseQueryOptions {
    period?: EnterprisePeriod;
    startDate?: string;
    endDate?: string;
    status?: 'all' | 'active';
    clubId?: string;
    limit?: number;
    page?: number;
}

export function buildEnterpriseQueryString(options: EnterpriseQueryOptions = {}): string {
    const params = new URLSearchParams();
    if (options.period) params.set('period', options.period);
    if (options.startDate) params.set('startDate', options.startDate);
    if (options.endDate) params.set('endDate', options.endDate);
    if (options.status) params.set('status', options.status);
    if (options.clubId) params.set('clubId', options.clubId);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.page) params.set('page', String(options.page));
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
}
